import React, { PropsWithChildren, useEffect } from 'react';

import { jwtDecode } from 'jwt-decode';
import Keycloak from 'keycloak-js';

import { KeycloakAuthProvider, useKeycloak } from '@/auth/KeycloakAuthState.ts';
import { LoadingScreen } from '@/components/loading/LoadingScreen.tsx';
import { apiDataSource } from '@/io/datasource/ApiDatasource.ts';
import { Login } from '@/pages/login/Login.tsx';

const env = window.origin.includes('staging') ? 'staging' : 'production';

const keycloakInstance = new Keycloak({
  url: import.meta.env.VITE_APP_AUTH_URL || 'https://auth.steto.care/auth/',
  realm: env === 'staging' ? 'steto-staging' : 'steto',
  clientId: import.meta.env.VITE_APP_AUTH_CLIENT_ID || 'steto-frontend',
});

const KeycloakInit: React.FC<PropsWithChildren> = ({ children }) => {
  const keycloak = useKeycloak(state => state);

  useEffect(() => {
    keycloakInstance.onAuthSuccess = () => {
      if (keycloakInstance.token) {
        keycloakInstance.token &&
          apiDataSource.createInstance(keycloakInstance.token);
        const decodedToken = jwtDecode(keycloakInstance.token);
        keycloak.setImpersonating('impersonator' in decodedToken);
      }
    };
    keycloakInstance.onTokenExpired = () => {
      keycloakInstance.updateToken(5);
    };
    const init = async () => {
      await keycloakInstance.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri:
          window.location.origin + '/silent-check-sso.html',
      });

      keycloak.setInitialized(true);
    };

    init();
  }, []);

  if (!keycloak.initialized) {
    return <LoadingScreen />;
  }

  if (!keycloak.keycloak.authenticated) {
    return <Login />;
  }

  return <>{children}</>;
};

export const KeycloakAuth: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <KeycloakAuthProvider
      keycloak={keycloakInstance}
      initialized={false}
      impersonating={false}
    >
      <KeycloakInit>{children}</KeycloakInit>
    </KeycloakAuthProvider>
  );
};
